import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  InfoBox,
  Input,
  Label,
  LoadingState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Toast,
  kstDotDateStr,
  kstTimeStr,
} from '@biznomi/ui-react'
import { attendanceTypeLabel } from '@biznomi/types'
import { useAuth } from '@/lib/auth/store'
import {
  approveDevice,
  getDepartments,
  getManagedDepartments,
  rejectDevice,
  updateManagedDepartments,
  getEmployee,
  getEmployeeDevices,
  getOrgAttendance,
  issueTempPassword,
  restoreEmployee,
  revokeDevice,
  terminateEmployee,
  updateEmployee,
  type EmployeeDevice,
} from '@/lib/api/admin'
import { getNfcMappings } from '@/lib/api/nfcMapping'
import { approveMessage } from '@/lib/device-approval'

/**
 * 직원 상세 — 관리자가 "이 사람 왜 이러지" 를 확인하는 곳.
 *
 * 목록에는 이름·부서·상태밖에 없어서, 문제가 생기면 어디서 봐야 할지가 없었다.
 * 30인+ 조직에서 관리자가 제일 자주 하는 일이 이것이라 한 화면에 모은다:
 *
 *   최근 태깅   실제로 찍고 있나
 *   기기        1기기 1사용자가 지켜지고 있나 (**쓸 수 있는** 기기 2대 이상이면 의심 신호)
 *   담당 근무지 제한이 걸려 있나, 어디로
 *   조치        임시 비번 · 퇴사 · 복직
 *
 * 판정은 하지 않는다 — 지각·조퇴를 여기서 세지 않는다. 사실만 모아 보여주고
 * 판단은 사람이 한다 (§원칙 2).
 */

const NONE = '__none__'

function statusBadge(d: EmployeeDevice) {
  if (d.status === 'APPROVED' && d.isActive) return <Badge variant="info">사용 중</Badge>
  if (d.status === 'APPROVED') return <Badge variant="outline">승인됨</Badge>
  if (d.status === 'PENDING') return <Badge variant="warning">승인 대기</Badge>
  if (d.status === 'REJECTED') return <Badge variant="destructive">거절됨</Badge>
  return <Badge variant="outline">해제됨</Badge>
}

/** 최근 30일 — 태깅이 도는지 보는 게 목적이라 기간을 길게 잡지 않는다 */
function recentRange() {
  const now = Date.now()
  const iso = (ms: number) => kstDotDateStr(ms).replace(/\./g, '-')
  return { from: iso(now - 29 * 86400_000), to: iso(now) }
}

export default function EmployeeDetail() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const isAdmin = useAuth((s) => s.user?.role) === 'ADMIN'
  const myId = useAuth((s) => s.user?.userId)

  const [editing, setEditing] = useState(false)
  const [toast, setToast] = useState('')
  const [error, setError] = useState('')
  const [tempPw, setTempPw] = useState<string | null>(null)
  const [confirmLeave, setConfirmLeave] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<EmployeeDevice | null>(null)
  // 승인 대기 기기의 승인·반려. 둘 다 되돌리려면 직원이 다시 등록해야 해서 한 번 묻는다
  const [pendingTarget, setPendingTarget] = useState<{ d: EmployeeDevice; act: 'approve' | 'reject' } | null>(null)
  const [busyDeviceId, setBusyDeviceId] = useState('')

  const { data: empData, isLoading } = useQuery({
    queryKey: ['employee', id],
    queryFn: () => getEmployee(id),
    enabled: !!id,
  })
  const emp = empData?.success ? empData.data : undefined

  const { data: devData } = useQuery({
    queryKey: ['employee', id, 'devices'],
    queryFn: () => getEmployeeDevices(id),
    enabled: !!id,
  })
  const devices = devData?.success ? devData.data ?? [] : []

  const range = recentRange()
  const { data: attData } = useQuery({
    queryKey: ['employee', id, 'attendance', range.from, range.to],
    queryFn: () => getOrgAttendance({ ...range, employeeId: id, take: 30 }),
    enabled: !!id,
  })
  const records = attData?.success ? attData.data ?? [] : []

  const { data: mapData } = useQuery({ queryKey: ['nfc-mappings'], queryFn: getNfcMappings })
  const myMapping = (mapData?.success ? mapData.data ?? [] : []).find((m) => m.employeeId === id)

  const { data: deptData } = useQuery({ queryKey: ['departments'], queryFn: getDepartments })
  const departments = deptData?.success ? deptData.data ?? [] : []

  // 담당 부서. 저장을 막는 규칙은 앞으로 생길 것만 막으므로,
  // **이미 매핑 없이 MANAGER 인 계정**은 여기서 드러내야 한다.
  const { data: managedData } = useQuery({
    queryKey: ['employee', id, 'managed-departments'],
    queryFn: () => getManagedDepartments(id),
    enabled: !!id,
  })
  const managedDepts = managedData?.success ? managedData.data ?? [] : []
  const managerWithoutDepts = emp?.role === 'MANAGER' && managedData?.success && managedDepts.length === 0

  /*
   * 🔴 `isActive` 를 같이 본다 (2026-09-07). 전에는 `status` 만 세서, 정상적인 폰 교체
   *   뒤에도 "폰 2대로 찍을 수 있습니다" 경고가 떴다. 사실이 아니다 — 서버는
   *   `status !== APPROVED || !isActive` 를 DEVICE_NOT_ACTIVE 로 막는다
   *   (`device.service.ts:213`). 꺼진 기기로는 못 찍는다.
   *
   *   같은 파일의 배지(`statusBadge`)는 처음부터 둘을 다 봤다(사용 중 / 승인됨).
   *   카운트만 기준이 달라서 화면 안에서 서로 다른 말을 했다.
   *
   * ⚠ 이 경고는 **대리출근 신호**다. 승인 버튼이 생기면서 폰을 바꿀 때마다 뜨게 됐는데,
   *   매번 울리는 신호는 아무도 안 본다. 진짜일 때만 울려야 값을 한다.
   */
  const activeCount = devices.filter((d) => d.status === 'APPROVED' && d.isActive).length

  async function doIssueTempPw() {
    setError('')
    const res = await issueTempPassword(id)
    if (!res.success || !res.data) {
      setError(res.error ?? '발급하지 못했습니다')
      return
    }
    setTempPw(res.data.tempPassword)
  }

  async function doTerminate() {
    setConfirmLeave(false)
    const res = emp?.isActive ? await terminateEmployee(id) : await restoreEmployee(id)
    if (!res.success) {
      setError(res.error ?? '처리하지 못했습니다')
      return
    }
    qc.invalidateQueries({ queryKey: ['employee', id] })
    qc.invalidateQueries({ queryKey: ['employees'] })
    setToast(emp?.isActive ? '퇴사 처리했습니다' : '복직 처리했습니다')
  }

  async function doRevoke() {
    if (!revokeTarget) return
    const res = await revokeDevice(revokeTarget.id)
    setRevokeTarget(null)
    if (!res.success) {
      setError(res.error ?? '해제하지 못했습니다')
      return
    }
    qc.invalidateQueries({ queryKey: ['employee', id, 'devices'] })
    setToast('해제했습니다. 이 폰으로는 이제 못 찍습니다')
  }

  /*
   * 승인 대기 기기 처리 (2026-09-07).
   *
   * 이 버튼이 없어서 9/7 에 prod DB 를 SQL 로 직접 고쳤고, 그 우회가 1기기 1사용자를
   * 깨뜨렸다 — `approveDevice()` 를 안 거치니 **옛 기기가 안 꺼졌다.** 감사 로그도 안 남았다.
   * 서버는 그때도 멀쩡했다. 막고 있던 건 버튼이 없다는 것 하나였다.
   *
   * ⚠ 실패는 서버 문구를 그대로 보여준다. ADMIN 이 아니면 403 이 오는데
   *   (`device.controller.ts:221`) 그때 "처리하지 못했습니다" 로 덮으면 왜 안 되는지가 사라진다.
   */
  async function doPendingAction() {
    if (!pendingTarget) return
    const { d, act } = pendingTarget
    setPendingTarget(null)
    setError('')
    setBusyDeviceId(d.id)
    const res = act === 'approve' ? await approveDevice(d.id) : await rejectDevice(d.id)
    setBusyDeviceId('')
    if (!res.success) {
      setError(res.error ?? (act === 'approve' ? '승인하지 못했습니다' : '반려하지 못했습니다'))
      return
    }
    qc.invalidateQueries({ queryKey: ['employee', id, 'devices'] })
    setToast(act === 'approve' ? '승인했습니다. 이 폰으로 찍을 수 있습니다' : '반려했습니다')
  }


  if (isLoading) return <LoadingState />
  if (!emp) return <EmptyState title="직원을 찾을 수 없습니다" />

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <button
            onClick={() => navigate('/admin/employees')}
            className="mb-1 text-xs text-slate-400 hover:text-slate-600"
          >
            ← 직원 목록
          </button>
          <h1 className="flex items-center gap-2 text-xl font-bold">
            {emp.name}
            {!emp.isActive && <Badge variant="outline">퇴사</Badge>}
            {emp.role === 'ADMIN' && <Badge variant="purple">최종관리자</Badge>}
            {emp.role === 'MANAGER' && <Badge variant="purple">관리자</Badge>}
          </h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {[emp.employeeNumber, emp.department?.name, emp.email].filter(Boolean).join(' · ') || '—'}
          </p>
        </div>
        {isAdmin && !editing && (
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            정보 수정
          </Button>
        )}
      </div>

      {error && (
        <p role="alert" className="rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      {/*
        관리자인데 담당 부서가 하나도 없는 계정.
        "저장을 막는" 규칙은 앞으로 생길 것만 막는다. 이미 이 상태인 계정은
        화면 어디에도 표시가 없어서 **본인이 신고하기 전까지 아무도 모른다.**
      */}
      {managerWithoutDepts && (
        <InfoBox tone="warning" title="담당 부서가 없어 아무것도 보이지 않습니다">
          관리자로 지정돼 있지만 맡은 부서가 없습니다. 이 상태로는 출퇴근 기록·직원·휴가
          승인이 전부 빈 화면으로 보입니다. 정보 수정에서 담당 부서를 골라 주세요.
        </InfoBox>
      )}

      {emp.role === 'MANAGER' && managedDepts.length > 0 && !editing && (
        <p className="text-sm text-slate-500">
          담당 부서 · {managedDepts.map((d) => d.name).join(' · ')}
        </p>
      )}

      {editing && isAdmin && (
        <EditCard
          emp={emp}
          departments={departments}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            qc.invalidateQueries({ queryKey: ['employee', id] })
            qc.invalidateQueries({ queryKey: ['employees'] })
            setToast('저장했습니다')
          }}
        />
      )}

      {/* ── 기본 사실 ─────────────────────────────── */}
      <section className="space-y-2 rounded-card bg-white p-4">
        <h2 className="text-sm font-bold text-slate-500">기본</h2>
        <dl className="grid grid-cols-2 gap-y-2 text-sm">
          <dt className="text-slate-400">입사일</dt>
          <dd>{kstDotDateStr(emp.hireDate)}</dd>
          <dt className="text-slate-400">비밀번호</dt>
          <dd>
            {emp.mustChangePassword ? (
              <span className="text-[var(--color-danger)]">아직 안 바꿈</span>
            ) : emp.passwordChangedAt ? (
              `${kstDotDateStr(emp.passwordChangedAt)} 변경`
            ) : (
              '—'
            )}
          </dd>
          <dt className="text-slate-400">개인정보 동의</dt>
          <dd>{emp.privacyConsentedAt ? kstDotDateStr(emp.privacyConsentedAt) : '미동의'}</dd>
          {!emp.isActive && emp.leftAt && (
            <>
              <dt className="text-slate-400">퇴사일</dt>
              <dd>{kstDotDateStr(emp.leftAt)}</dd>
            </>
          )}
        </dl>
      </section>

      {/* ── 기기 ─────────────────────────────────── */}
      <section className="space-y-2 rounded-card bg-white p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-500">기기 ({devices.length})</h2>
          {activeCount > 1 && <Badge variant="warning">쓸 수 있는 기기 {activeCount}대</Badge>}
        </div>

        {/*
          1기기 1사용자가 대리출근 차단의 뼈대다. 승인된 기기가 둘 이상이면
          "빌려준 폰으로 대신 찍기"가 가능한 상태라 관리자가 알아야 한다.
          자동으로 막지는 않는다 — 기기 교체 직후에도 잠깐 2대가 되기 때문.
        */}
        {activeCount > 1 && (
          <InfoBox tone="warning">
            이 직원이 폰 {activeCount}대로 찍을 수 있습니다. 폰을 막 바꾼 게 아니라면 안 쓰는 쪽은
            해제해 주세요. 두 대를 다 쓸 수 있으면 한 대를 남에게 맡겨 대신 찍게 할 수 있습니다.
          </InfoBox>
        )}

        {devices.length === 0 ? (
          <p className="py-3 text-sm text-slate-400">등록된 기기가 없습니다.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {devices.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate text-sm font-medium">{d.deviceName || '이름 없는 기기'}</p>
                    {statusBadge(d)}
                  </div>
                  <p className="truncate text-xs text-slate-400">
                    {[d.platform, `등록 ${kstDotDateStr(d.createdAt)}`,
                      d.lastUsedAt ? `마지막 사용 ${kstDotDateStr(d.lastUsedAt)}` : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                {/*
                  ⚠ 역할로 **숨기기만** 한다. 막는 건 서버다 (ADMIN 아니면 403).
                    화면을 유일한 문지기로 두면 API 를 직접 부르는 길이 그대로 열린다.
                */}
                {isAdmin && d.status === 'PENDING' && (
                  <div className="flex shrink-0 gap-1.5">
                    <Button
                      size="sm"
                      className="shrink-0"
                      disabled={busyDeviceId === d.id}
                      onClick={() => setPendingTarget({ d, act: 'approve' })}
                    >
                      승인
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="shrink-0"
                      disabled={busyDeviceId === d.id}
                      onClick={() => setPendingTarget({ d, act: 'reject' })}
                    >
                      반려
                    </Button>
                  </div>
                )}
                {isAdmin && d.status === 'APPROVED' && (
                  <Button variant="secondary" size="sm" className="shrink-0" onClick={() => setRevokeTarget(d)}>
                    해제
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── 담당 근무지 ──────────────────────────── */}
      <section className="space-y-2 rounded-card bg-white p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-500">담당 근무지</h2>
          {isAdmin && (
            <Button variant="secondary" size="sm" onClick={() => navigate('/admin/nfc-mappings')}>
              변경
            </Button>
          )}
        </div>
        {myMapping ? (
          <div className="flex flex-wrap gap-1">
            {myMapping.spots.map((s) => (
              <Badge key={s.id} variant="purple">
                {s.location ? `${s.location} ${s.name}` : s.name}
              </Badge>
            ))}
          </div>
        ) : (
          // 없음 = 제한 없음. 이걸 "미설정"으로만 쓰면 반대로 읽힌다
          <p className="text-sm text-slate-500">
            정해 둔 곳이 없어서 <b>어디서나</b> 찍을 수 있습니다.
          </p>
        )}
      </section>

      {/* ── 최근 태깅 ────────────────────────────── */}
      <section className="space-y-2 rounded-card bg-white p-4">
        <h2 className="text-sm font-bold text-slate-500">최근 30일 ({records.length}번 찍음)</h2>
        {records.length === 0 ? (
          <p className="py-3 text-sm text-slate-400">기록이 없습니다.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {records.slice(0, 15).map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span className="text-slate-500">{kstDotDateStr(r.timestamp)}</span>
                <span className="flex items-center gap-1.5">
                  <Badge variant={r.type === 'CHECKIN' ? 'info' : 'outline'}>
                    {attendanceTypeLabel(r.type)}
                  </Badge>
                  {r.isManual && <Badge variant="warning">수기</Badge>}
                  <span className="tabular-nums">{kstTimeStr(r.timestamp)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── 조치 ─────────────────────────────────── */}
      {isAdmin && (
        <section className="space-y-3 rounded-card bg-white p-4">
          <h2 className="text-sm font-bold text-slate-500">조치</h2>

          {tempPw && (
            <InfoBox tone="warning">
              임시 비밀번호는 <b className="font-mono">{tempPw}</b> 입니다. 이 화면을 벗어나면 다시 못 봅니다.
              본인에게 직접 알려 주고, 로그인하면 바로 바꾸라고 하세요.
            </InfoBox>
          )}

          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => void doIssueTempPw()}>
              임시 비밀번호 발급
            </Button>
            {/* 본인 퇴사는 막는다 — 마지막 관리자면 조직이 잠긴다 */}
            {emp.id !== myId && (
              <Button variant="secondary" size="sm" onClick={() => setConfirmLeave(true)}>
                {emp.isActive ? '퇴사 처리' : '복직 처리'}
              </Button>
            )}
          </div>
        </section>
      )}

      {confirmLeave && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setConfirmLeave(false)}
          title={emp.isActive ? '퇴사 처리할까요?' : '복직 처리할까요?'}
          message={
            emp.isActive
              ? `${emp.name} 님은 로그인도 태깅도 못 하게 됩니다. 지금까지 기록은 그대로 남습니다.`
              : `${emp.name} 님이 다시 로그인하고 찍을 수 있게 됩니다.`
          }
          confirmText={emp.isActive ? '퇴사 처리' : '복직 처리'}
          destructive={emp.isActive}
          onConfirm={() => void doTerminate()}
        />
      )}

      {revokeTarget && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setRevokeTarget(null)}
          title="기기를 해제할까요?"
          message={`${revokeTarget.deviceName || '이 폰'}으로는 더 이상 못 찍습니다. 본인이 다시 등록하면 관리자가 한 번 더 승인해야 합니다.`}
          confirmText="해제"
          destructive
          onConfirm={() => void doRevoke()}
        />
      )}

      {/*
        승인은 되돌리기가 "해제" 라 가볍지만, **다른 폰이 그 자리에서 꺼진다.**
        그건 누르는 사람이 모르는 결과라 미리 말해 준다 (1기기 1사용자).
      */}
      {pendingTarget && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setPendingTarget(null)}
          title={pendingTarget.act === 'approve' ? '이 기기를 승인할까요?' : '이 기기를 반려할까요?'}
          message={
            pendingTarget.act === 'approve'
              ? approveMessage(emp.name, pendingTarget.d, devices)
              : `${pendingTarget.d.deviceName || '이 폰'}으로는 출퇴근을 찍을 수 없습니다. 본인이 다시 등록하면 여기서 한 번 더 승인해야 합니다.`
          }
          confirmText={pendingTarget.act === 'approve' ? '승인' : '반려'}
          destructive={pendingTarget.act === 'reject'}
          onConfirm={() => void doPendingAction()}
        />
      )}

      {toast && <Toast message={toast} variant="success" onClose={() => setToast('')} />}
    </div>
  )
}

/** 이름·사번·역할·부서만 고친다 (서버 UpdateEmployeeSchema 와 같은 범위) */
function EditCard({
  emp,
  departments,
  onCancel,
  onSaved,
}: {
  emp: { id: string; name: string; employeeNumber: string | null; role: 'ADMIN' | 'MANAGER' | 'EMPLOYEE'; department?: { name: string } | null; departmentId: string | null }
  departments: { id: string; name: string }[]
  onCancel: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(emp.name)
  const [num, setNum] = useState(emp.employeeNumber ?? '')
  const [role, setRole] = useState(emp.role)
  const [dept, setDept] = useState(emp.departmentId ?? NONE)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // 지금 맡고 있는 부서. MANAGER 로 바꿀 때 이 값을 함께 보낸다.
  const { data: managedData } = useQuery({
    queryKey: ['employee', emp.id, 'managed-departments'],
    queryFn: () => getManagedDepartments(emp.id),
  })
  const [managed, setManaged] = useState<string[] | null>(null)
  const currentManaged = managedData?.success ? (managedData.data ?? []).map((d) => d.id) : []
  // 사용자가 아직 안 건드렸으면 서버 값을 그대로 쓴다
  const picked = managed ?? currentManaged
  const toggle = (id: string) =>
    setManaged(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id])

  // ★ 이 화면이 존재하는 이유. 역할만 바꾸고 부서를 안 고르면
  //   아무것도 못 보는 관리자가 만들어진다. 그래서 저장을 막는다.
  const needsDepartments = role === 'MANAGER' && picked.length === 0

  async function save() {
    if (!name.trim()) return setError('이름을 입력해 주세요')
    if (needsDepartments) return setError('담당 부서를 하나 이상 선택해 주세요')
    setSaving(true)
    setError('')
    const res = await updateEmployee(emp.id, {
      name: name.trim(),
      employeeNumber: num.trim() || null,
      role,
      departmentId: dept === NONE ? null : dept,
      // 역할이 안 바뀌어도 같이 보낸다. 서버가 역할 변경 여부로 판단한다
      ...(role === 'MANAGER' ? { managedDepartmentIds: picked } : {}),
    })
    setSaving(false)
    if (!res.success) {
      setError(res.error ?? '저장하지 못했습니다')
      return
    }
    // 역할이 그대로면 서버가 매핑을 안 건드리므로 여기서 따로 저장한다
    if (role === 'MANAGER' && emp.role === 'MANAGER') {
      const m = await updateManagedDepartments(emp.id, picked)
      if (!m.success) {
        setError(m.error ?? '담당 부서를 저장하지 못했습니다')
        return
      }
    }
    onSaved()
  }

  return (
    <section className="space-y-3 rounded-card bg-white p-4">
      <h2 className="text-sm font-bold text-slate-500">정보 수정</h2>

      <div className="space-y-1.5">
        <Label htmlFor="emp-name">이름</Label>
        <Input id="emp-name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="emp-num">사번</Label>
        <Input id="emp-num" value={num} onChange={(e) => setNum(e.target.value)} placeholder="없으면 비워 두세요" />
      </div>

      <div className="space-y-1.5">
        <Label>역할</Label>
        <Select value={role} onValueChange={(v) => setRole(v as typeof role)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="EMPLOYEE">직원</SelectItem>
            <SelectItem value="MANAGER">관리자 (담당 부서만)</SelectItem>
            <SelectItem value="ADMIN">최종관리자 (전체)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label>부서</Label>
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>부서 없음</SelectItem>
            {departments.map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/*
        담당 부서 — 역할이 관리자일 때만 나온다.
        여기서 안 고르면 저장을 막는다. 역할만 바꾸고 끝낼 수 있게 두면
        아무것도 못 보는 관리자가 생긴다.
        한 부서에 관리자가 여럿이어도 된다 (서버 @@id([departmentId, employeeId])).
      */}
      {role === 'MANAGER' && (
        <div className="space-y-1.5">
          <Label>담당 부서</Label>
          {departments.length === 0 ? (
            <InfoBox tone="warning">
              부서가 없습니다. 부서 화면에서 먼저 만들어 주세요.
            </InfoBox>
          ) : (
            <div className="space-y-1 rounded-lg border border-slate-200 p-2">
              {departments.map((d) => (
                <label
                  key={d.id}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={picked.includes(d.id)}
                    onChange={() => toggle(d.id)}
                  />
                  {d.name}
                </label>
              ))}
            </div>
          )}
          <p className="text-xs leading-relaxed text-slate-400">
            고른 부서의 출퇴근·직원·휴가만 봅니다. 하나도 안 고르면 아무것도 보이지 않아서
            저장할 수 없습니다.
          </p>
        </div>
      )}

      {error && <p role="alert" className="text-sm text-[var(--color-danger)]">{error}</p>}

      <div className="flex gap-2">
        <Button variant="secondary" className="flex-1" onClick={onCancel} disabled={saving}>
          취소
        </Button>
        <Button className="flex-1" loading={saving} disabled={needsDepartments} onClick={() => void save()}>
          저장
        </Button>
      </div>
    </section>
  )
}
